"use client";

import { X, Mail, Sparkles } from "lucide-react";
import Link from "next/link";

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  therapistName: string;
  therapistEmail: string;
}

export function WaitlistModal({ isOpen, onClose, therapistName, therapistEmail }: WaitlistModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">
            Join {therapistName}&apos;s Waitlist
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Email Option */}
          <div className="bg-gray-50 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-primary-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Contact {therapistName} directly</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Send an email to express your interest and ask about their availability.
                </p>
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 mb-3">
                  <p className="text-xs text-gray-500 mb-0.5">{therapistName}&apos;s email</p>
                  <p className="text-sm text-gray-900 font-medium">{therapistEmail}</p>
                </div>
                <a
                  href={`mailto:${therapistEmail}?subject=Interest in joining your waitlist&body=Hi ${therapistName},%0D%0A%0D%0AI'm interested in working with you and would like to join your waitlist. Please let me know when you have availability.%0D%0A%0D%0AThank you!`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Email {therapistName}
                </a>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* Matching Quiz Option */}
          <div className="bg-gradient-to-br from-primary-50 to-cyan-50 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                <Sparkles className="w-5 h-5 text-primary-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Find your perfect match</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Take our matching quiz to discover therapists who are currently available and match your needs.
                </p>
                <Link
                  href="/quiz"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-primary-600 text-sm font-medium rounded-full border border-primary-200 hover:bg-primary-50 transition-colors shadow-sm"
                >
                  <Sparkles className="w-4 h-4" />
                  Take the Matching Quiz
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            We&apos;ll help you find the right therapist for your journey.
          </p>
        </div>
      </div>
    </div>
  );
}
